/**
 * document-select-labels.ts — etykiety list wyboru „status" i „pilność" DOKUMENTÓW, które nie są
 * słownikiem tenanta, tylko ZAMKNIĘTYM enumem zapisanym w kodzie (przeciwnie do pilności i sposobu
 * płatności faktur, które mają słownik — te idą ścieżką słownikową, patrz `useListFilterOptions`).
 *
 * Kody (`DOCUMENT_URGENCIES`, `LETTER_STATUSES`, `STANDARD_DOCUMENT_STATUSES`,
 * `CONTRACT_DOCUMENT_STATUSES`, `CONTRACT_STATUSES`, `CONTRACT_PERIOD_UNITS`) żyją w `@saas/shared`
 * (`core-field-domains`) — jedno źródło dla formularzy kartotek, kolumn i filtrów list oraz edytora
 * wartości domyślnej. Tu są tylko nazwy kodów.
 *
 * Używają ich formularze kartotek (listy wyboru) i kolumny list (`standard-document-columns`,
 * `letter-columns`, `contract-columns` przez `buildStaticSelectOverride`): ta sama funkcja etykiety
 * zasila pole na formularzu, komórkę kolumny i opcje listy wyboru w wierszu filtrów, więc użytkownik
 * wybiera i filtruje po dokładnie tych nazwach, które widzi na karcie i w wierszach.
 *
 * Test `document-select-labels.test.ts` pilnuje, że każdy kod ma w `pl.json` prawdziwe tłumaczenie
 * (klucze budowane z kodu są martwym polem skanera `check:i18n`, więc brak tłumaczenia sam by się
 * nie ujawnił) — to ŚWIADOMY wyjątek od reguły z `ksef-jpk-markers.ts` (literał `t()` per gałąź);
 * strażnikiem jest ten test.
 *
 * Kod spoza listy (dane historyczne) pokazujemy surowo (`defaultValue` = kod).
 */

import type { CoreColumnTranslateFn } from '@/lib/ext-list-columns';

/** Etykieta pilności dokumentów handlowych i wewnętrznych (klucze wspólne dokumentów). */
export function documentUrgencyLabel(code: string, t: CoreColumnTranslateFn): string {
  return t(`tenant.documents.shared.urgencyValues.${code}`, { defaultValue: code });
}

/** Etykieta statusu dokumentów handlowych i wewnętrznych. */
export function standardDocumentStatusLabel(code: string, t: CoreColumnTranslateFn): string {
  return t(`tenant.documents.shared.statusValues.${code}`, { defaultValue: code });
}

/** Etykieta statusu pisma. */
export function letterStatusLabel(code: string, t: CoreColumnTranslateFn): string {
  return t(`tenant.documents.letters.detail.statusValues.${code}`, { defaultValue: code });
}

/** Etykieta pilności pisma — własne klucze (forma „Pilne", nie „Pilna" jak we wspólnych). */
export function letterUrgencyLabel(code: string, t: CoreColumnTranslateFn): string {
  return t(`tenant.documents.letters.detail.urgencyValues.${code}`, { defaultValue: code });
}

/** Etykieta „Status dokumentu” umowy. */
export function contractDocumentStatusLabel(code: string, t: CoreColumnTranslateFn): string {
  return t(`tenant.documents.contracts.detail.statusValues.${code}`, { defaultValue: code });
}

/** Etykieta „Status umowy” (stan umowy). */
export function contractStatusLabel(code: string, t: CoreColumnTranslateFn): string {
  return t(`tenant.documents.contracts.detail.contractStatusValues.${code}`, {
    defaultValue: code,
  });
}

/** Etykieta jednostki czasu trwania umowy. */
export function contractPeriodUnitLabel(code: string, t: CoreColumnTranslateFn): string {
  return t(`tenant.documents.contracts.detail.periodUnitValues.${code}`, { defaultValue: code });
}
